
import { useState, useEffect, useRef } from 'react';

export default function ProjectsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      title: '네이버 스마트 스토어 API 기반 메시지 발송 웹서비스',
      period: '2025.08 ~ 2025.12',
      description: '네이버 스마트 스토어와 연동된 메시지 발송 시스템 구축',
      icon: 'ri-store-2-line',
      badge: 'E-Commerce',
      badgeColor: 'from-green-500 to-emerald-500',
      details: [
        '네이버 스마트 스토어 API 연동, 상품 상태 변경 이벤트 감지 및 데이터 동기화 스케줄러 개발',
        '카카오/네이버 메시지 발송 및 통계 수집 로직 개발',
        'PG 연동 결제 시스템 개발 및 네이버 비즈월렛 연동',
        '개발/운영환경 인프라 및 Jenkins, Docker 기반 CI/CD 파이프라인 구축'
      ],
      tags: ['Spring Boot', 'Naver API', 'Jenkins', 'Docker', 'PG연동']
    },
    {
      title: 'SKT 고객 데이터 기반 문자 메시지 발송 웹서비스',
      period: '2024.06 ~ 2024.11',
      description: 'SKT 고객 데이터를 활용한 타겟 메시지 발송 시스템',
      icon: 'ri-message-2-line',
      badge: 'Messaging',
      badgeColor: 'from-blue-500 to-cyan-500',
      details: [
        'SKT 문자 메시지 발송 API 연동, 웹/앱 접속 이력, 통화 이력 및 위치 기반 문자 메시지 발송 기능 연동',
        'JWT 기반 인증/인가 환경 구축 및 카카오/네이버/카페24 OAuth 연동',
        'PG 연동 결제 시스템 개발',
        '발송 통계 조회 서비스 개발',
        '서비스 운영 및 관리를 위한 백오피스 풀스택 개발'
      ],
      tags: ['SKT API', 'JWT', 'OAuth', 'Spring Boot', 'React']
    },
    {
      title: '카카오톡 메시지 발송을 위한 채널/메시지 관리 API 서비스',
      period: '2024.12 ~ 2025.12',
      description: '카카오톡 비즈니스 메시지 관리 및 발송 API 시스템',
      icon: 'ri-chat-3-line',
      badge: 'API Service',
      badgeColor: 'from-yellow-500 to-orange-500',
      details: [
        'API 유지보수 및 신규 기능 개발, CS 대응',
        'AOP 기반 로그 구조 개편, 유형별 로그 파일 분리',
        'Nginx 기반 서비스 이중화 및 Jenkins, Docker 기반 CI/CD 파이프라인 구축',
        'Java 기반 데이터 동기화 애플리케이션을 Go로 마이그레이션, Java 대비 성능 5배 개선'
      ],
      tags: ['KakaoTalk API', 'Go', 'Nginx', 'AOP', 'Performance']
    },
    {
      title: '스포츠 커뮤니티 웹서비스',
      period: '2023.12 ~ 2024.02',
      description: '실시간 채팅과 상품 거래 기능을 갖춘 스포츠 커뮤니티 플랫폼',
      icon: 'ri-football-line',
      badge: 'Community',
      badgeColor: 'from-purple-500 to-pink-500',
      details: [
        'WebSocket, MongoDB, Kafka 기반 실시간 채팅 기능 개발',
        '쿼리 최적화, 테이블 파티셔닝을 이용한 상품 검색 서비스 성능 98% 개선',
        '대규모 동시 트래픽 대응을 위한 상품 구매 서비스 동시성 제어 로직 개발',
        'nGrinder 기반 스트레스 테스트 환경 구축',
        'AWS 기반 클라우드 인프라 구축, Github, Docker 기반 CI/CD 파이프라인 구축'
      ],
      tags: ['WebSocket', 'MongoDB', 'Kafka', 'AWS', 'Performance Tuning']
    },
    {
      title: 'ChatGPT 기반 맞춤형 운동추천 웹서비스',
      period: '2023.03 ~ 2023.10',
      description: 'AI를 활용한 개인 맞춤형 운동 루틴 추천 시스템',
      icon: 'ri-brain-line',
      badge: 'AI Service',
      badgeColor: 'from-teal-500 to-green-500',
      details: [
        'ChatGPT API를 이용한 운동 추천 시스템 개발',
        'JWT 기반 인증/인가 환경 구축',
        'AWS 기반 클라우드 인프라 구축, Github, Docker 기반 CI/CD 파이프라인 구축'
      ],
      tags: ['ChatGPT API', 'JWT', 'AWS', 'Spring Boot']
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-[#0a0e13]">
      <div className="max-w-7xl mx-auto">
        <div className={`transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            <span className="bg-gradient-to-r from-[#3ECF8E] to-[#2dd4bf] bg-clip-text text-transparent">
              Projects
            </span>
          </h2>

          <div className="space-y-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`group bg-[#161b22] border border-[#30363d] rounded-xl p-8 hover:border-[#3ECF8E]/50 transition-all duration-500 hover:shadow-lg hover:shadow-[#3ECF8E]/10 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Project Header */}
                  <div className="lg:w-1/3">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#3ECF8E] to-[#2dd4bf] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <i className={`${project.icon} text-xl text-black`} />
                      </div>
                      <div className="flex-1">
                        <span className={`inline-block px-3 py-1 bg-gradient-to-r ${project.badgeColor} text-white text-xs font-medium rounded-full mb-2`}>
                          {project.badge}
                        </span>
                        <div className="text-sm text-[#3ECF8E] font-mono">{project.period}</div>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#3ECF8E] transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  {/* Project Details */}
                  <div className="lg:w-2/3">
                    <div className="space-y-3 mb-6">
                      {project.details.map((detail, detailIndex) => (
                        <div
                          key={detailIndex}
                          className="flex items-start gap-3 text-sm text-gray-300"
                        >
                          <i className="ri-arrow-right-s-line text-[#3ECF8E] mt-0.5 flex-shrink-0" />
                          <span className="leading-relaxed">{detail}</span>
                        </div>
                      ))}
                    </div>

                    {/* Tech Tags */}
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-3 py-1.5 bg-[#0d1117] border border-[#30363d] rounded-md text-xs text-gray-400 hover:border-[#3ECF8E]/50 hover:text-[#3ECF8E] transition-all duration-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#3ECF8E]/5 to-[#2dd4bf]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>

          {/* Additional Info */}
          {/* <div className="mt-16 text-center">
            <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-8 max-w-4xl mx-auto hover:border-[#3ECF8E]/50 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-4">
                <span className="text-[#3ECF8E]">메시징 시스템</span>과 <span className="text-[#3ECF8E]">성능 최적화</span>에 특화
              </h3>
              <p className="text-gray-300 leading-relaxed">
                대규모 메시징 시스템 개발 및 운영 경험을 바탕으로, 
                <strong className="text-white"> API 연동</strong>, <strong className="text-white">실시간 처리</strong>, 
                <strong className="text-white"> 성능 최적화</strong> 등 
                백엔드 개발의 핵심 영역에서 검증된 역량을 보유하고 있습니다.
              </p>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
}
